/**
 * A page as the canvas draws it (`proto/Volunteers.dc.html`): the header block — eyebrow
 * (season · view · section), title, lede, actions to the right — then the stat-tile row and
 * the two-column cards. Styles in `page.css`.
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface PageHeaderProps {
  eyebrow: string;
  title: string;
  lede?: ReactNode;
  actions?: ReactNode;
}

/**
 * **What sits under the header's hairline** (Rebuild 37, the part row; James, Oct 16): a view
 * that folds several screens into one page provides its row here, and every header on that page
 * draws it under the charter — the screen itself never has to know.
 */
export const PageHeaderTail = createContext<ReactNode>(null);

export function PageHeader({ eyebrow, title, lede, actions }: PageHeaderProps): ReactNode {
  const tail = useContext(PageHeaderTail);
  return (
    <div className="ui-page-head">
      <div className="ui-page-head__eyebrow">{eyebrow}</div>
      <div className="ui-page-head__row">
        <div className="ui-page-head__text">
          <h1 className="ui-page-head__title">{title}</h1>
          {lede ? <p className="ui-page-head__lede">{lede}</p> : null}
        </div>
        {actions ? <div className="ui-page-head__actions">{actions}</div> : null}
      </div>
      {tail ? <div className="ui-page-head__tail">{tail}</div> : null}
    </div>
  );
}

/**
 * **The part row** (Rebuild 37): quiet uppercase parts under the charter hairline — the current
 * part underlined in red, a count beside a part where one helps, the reason on hover. Never a
 * filled button.
 */
export function PartRow({
  label,
  parts,
}: {
  label: string;
  parts: readonly {
    href: string;
    label: string;
    current: boolean;
    n?: string | null;
    why?: string | null;
  }[];
}): ReactNode {
  return (
    <nav aria-label={label} className="ui-parts">
      {parts.map((p) => (
        <a
          key={p.href}
          href={p.href}
          title={p.why ?? undefined}
          aria-current={p.current ? 'page' : undefined}
          className={`ui-parts__part${p.current ? ' ui-parts__part--current' : ''}`}
        >
          {p.label}
          {p.n ? <span className="ui-parts__n">{p.n}</span> : null}
        </a>
      ))}
    </nav>
  );
}

/** The page body under the header: the canvas's 22px 26px 40px. */
export function PageBody({ children }: { children: ReactNode }): ReactNode {
  return <div className="ui-page-body">{children}</div>;
}

export interface StatTile {
  /** The number as text — `NONE`, `– OF –`, `9`, `$3,118`. Never a guessed zero. */
  n: string;
  label: string;
  sub?: string;
  /** The number's color: mute (default) · navy · red · green · gold. */
  tone?: 'mute' | 'navy' | 'red' | 'green' | 'gold';
}

/** The stat-tile row: one card, tiles side by side with a hairline between. */
export function StatTiles({ tiles }: { tiles: readonly StatTile[] }): ReactNode {
  return (
    <div className="ui-tiles" role="list">
      {tiles.map((t, i) => (
        <div key={i} className="ui-tile" role="listitem">
          <div className={`ui-tile__n ui-tile__n--${t.tone ?? 'mute'}`}>{t.n}</div>
          <div className="ui-tile__label">{t.label}</div>
          {t.sub ? <div className="ui-tile__sub">{t.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

export interface CardProps {
  head: string;
  children: ReactNode;
  /** Sentence under the rows, as the canvas's cards close. */
  foot?: ReactNode;
  /**
   * One chip at the right-hand end of the title, for a count or a standing that belongs to the
   * whole card (James, Oct 1, batch 1b item 6). The festival kit read "FESTIVAL KIT · 2026"
   * with "2026 · 0 OF 4" in a legend underneath it — the year twice and the count in a
   * fieldset inside the card. It is still **not** a note beside the title (the Sep 21 pass):
   * a chip is a count or a state, in a word or two, and never a sentence.
   */
  chip?: ReactNode;
}

/** A card is its title, one chip, and its rows — no note beside the title (the Sep 21 pass). */
export function Card({ head, children, foot, chip }: CardProps): ReactNode {
  return (
    <section className="ui-card" aria-label={head}>
      <div className="ui-card__head">
        <span className="ui-card__title">{head}</span>
        {chip ?? null}
      </div>
      <div className="ui-card__body">{children}</div>
      {foot ? <div className="ui-card__foot">{foot}</div> : null}
    </section>
  );
}

/** Two columns, 14px apart, one column on a phone. */
export function CardGrid({ children }: { children: ReactNode }): ReactNode {
  return <div className="ui-card-grid">{children}</div>;
}

export interface CardRowProps {
  a: ReactNode;
  b?: ReactNode;
  c?: ReactNode;
  amount?: ReactNode;
  chip?: ReactNode;
  /**
   * What can be done about this row, at its right-hand end.
   *
   * A card row that lists something a person has to answer needs somewhere to answer it, and
   * the only right-hand slots were an amount and a chip — so the first screen that wanted a
   * button put one in the chip, which is a Chip's place. This is its own slot.
   */
  actions?: ReactNode;
}

/**
 * One row of a card: the thing · the detail · the sentence, then an amount, a chip and whatever
 * can be done about it, to the right.
 */
export function CardRow({ a, b, c, amount, chip, actions }: CardRowProps): ReactNode {
  return (
    <div className="ui-card-row">
      <div className="ui-card-row__text">
        <div className="ui-card-row__a">
          {a}
          {b ? <span className="ui-card-row__b"> · {b}</span> : null}
        </div>
        {c ? <div className="ui-card-row__c">{c}</div> : null}
      </div>
      {amount ? <div className="ui-card-row__amount">{amount}</div> : null}
      {chip ? <div className="ui-card-row__chip">{chip}</div> : null}
      {actions ? <div className="ui-card-row__actions">{actions}</div> : null}
    </div>
  );
}
